import { useState } from 'react';

export function CodeBlock({ label, commands }: { label: string; commands: string[] }) {
    const [copied, setCopied] = useState(false);
    const text = commands.join('\n');

    const handleCopy = () => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="my-2 rounded-lg overflow-hidden bg-slate-900 dark:bg-gray-900 border border-slate-800 dark:border-white/10 shadow-sm">
            <div className="flex items-center justify-between px-3 py-2 bg-slate-800/80 dark:bg-white/[0.02] border-b border-slate-700/60 dark:border-white/5">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-300 dark:text-gray-200">{label}</span>
                <button
                    onClick={handleCopy}
                    className={`px-2.5 py-0.5 text-[10px] font-semibold rounded-md transition-all cursor-pointer ${copied
                        ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                        : 'bg-slate-700 text-slate-200 border border-transparent hover:bg-slate-600 hover:text-white dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700'
                        }`}
                >
                    {copied ? '✓ Copied' : 'Copy'}
                </button>
            </div>
            <div className="p-2.5 font-mono text-[12px] leading-relaxed text-slate-200 dark:text-gray-200 overflow-x-auto">
                {commands.map((cmd, i) => (
                    <div key={i} className="whitespace-pre">
                        <span className="text-orange-400 select-none">$ </span>
                        {cmd}
                    </div>
                ))}
            </div>
        </div>
    );
}

export function StepItem({ num, title, children }: { num: number; title: string; children: React.ReactNode }) {
    return (
        <div className="flex gap-4 relative group/step">
            {/* Connector line */}
            <div className="absolute left-[13.5px] top-7 bottom-[-16px] w-[1px] bg-orange-500/20 group-last/step:hidden"></div>

            <div className="relative z-10 w-7 h-7 min-w-[28px] bg-orange-50 dark:bg-gray-800/80 border-2 border-orange-500 rounded-full flex items-center justify-center text-xs font-bold text-orange-600 dark:text-orange-400 mt-0.5 shadow-sm">
                {num}
            </div>

            <div className="flex-1 pb-1">
                <div className="text-[14px] font-bold text-slate-900 dark:text-gray-100 mb-1">{title}</div>
                <div className="text-[13px] text-slate-700 dark:text-gray-200 leading-relaxed space-y-1.5">{children}</div>
            </div>
        </div>
    );
}

export function AlertBox({ type, icon, children }: { type: string; icon: React.ReactNode; children: React.ReactNode }) {
    let styleClasses = '';

    switch (type) {
        case 'note':
            styleClasses = 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-500/10 dark:border-blue-500/20 dark:text-blue-400';
            break;
        case 'warning':
            styleClasses = 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-yellow-500/10 dark:border-yellow-500/20 dark:text-yellow-400';
            break;
        case 'success':
            styleClasses = 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-green-500/10 dark:border-green-500/20 dark:text-green-400';
            break;
        case 'error':
            styleClasses = 'bg-rose-50 border-rose-200 text-rose-800 dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-500';
            break;
        default:
            styleClasses = 'bg-slate-100 border-slate-200 text-slate-800 dark:bg-gray-500/10 dark:border-gray-500/20 dark:text-gray-200';
    }

    return (
        <div className={`my-2 p-2.5 rounded-lg flex gap-2.5 text-[13px] leading-relaxed border ${styleClasses}`}>
            <span className="text-base shrink-0 mt-[1px]">{icon}</span>
            <div>{children}</div>
        </div>
    );
}

export function Screenshot({ src, alt }: { src: string; alt: string }) {
    return (
        <div className="mt-2.5 overflow-hidden rounded-xl bg-slate-100 dark:bg-gray-950/80 border border-slate-300 dark:border-orange-500/40 shadow-md dark:shadow-xl shadow-slate-200/60 dark:shadow-black/50 max-w-sm transition-all duration-300 hover:shadow-orange-500/20 hover:border-orange-500/50 group relative">
            <div className="h-5 bg-slate-200 dark:bg-gray-900 border-b border-slate-300 dark:border-white/5 flex items-center px-3 gap-1.5 opacity-90">
                <div className="w-2 h-2 rounded-full bg-red-500/80 transition-colors duration-300"></div>
                <div className="w-2 h-2 rounded-full bg-yellow-500/80 transition-colors duration-300"></div>
                <div className="w-2 h-2 rounded-full bg-green-500/80 transition-colors duration-300"></div>
                <div className="ml-1.5 text-[9px] text-slate-600 dark:text-gray-200 font-medium tracking-widest truncate">{alt}</div>
            </div>
            <div className="bg-slate-50 dark:bg-gray-950/50">
                <img src={src} alt={alt} className="w-full h-auto opacity-90 dark:opacity-70 group-hover:opacity-100 transition-opacity duration-300" loading="lazy" />
            </div>
            {/* Glossy overlay effect for compact sleek look */}
            <div className="absolute inset-0 bg-gradient-to-tr from-white/0 via-white/[0.04] dark:via-white/[0.02] to-white/0 pointer-events-none"></div>
        </div>
    );
}

export function ScreenshotGrid({ children }: { children: React.ReactNode }) {
    return (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl">
            {children}
        </div>
    );
}

export function BulletList({ items }: { items: (string | React.ReactNode)[] }) {
    return (
        <ul className="mt-2 space-y-2">
            {items.map((item, i) => (
                <li key={i} className="flex gap-2.5 text-[13px] text-slate-700 dark:text-gray-300 leading-relaxed group/bullet">
                    <span className="text-orange-500 font-bold shrink-0 mt-0.5 transition-transform group-hover/bullet:translate-x-0.5">›</span>
                    <span className="flex-1">{item}</span>
                </li>
            ))}
        </ul>
    );
}
